import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Drawer } from '../shared/Drawer.jsx';
import { useCreateDevice, useUpdateDevice } from '../../hooks/useDevices.js';

const TYPES = [
  { key: 'rfid_reader', label: 'قارئ RFID' },
  { key: 'printer', label: 'طابعة' },
  { key: 'gate', label: 'بوابة' },
  { key: 'pos', label: 'POS' },
  { key: 'scanner', label: 'ماسح' }
];

function defaults(d) {
  return {
    name: d?.name || '',
    deviceId: d?.deviceId || '',
    serialNumber: d?.serialNumber || '',
    type: d?.type || 'rfid_reader',
    location: d?.location || '',
    ipAddress: d?.ipAddress || '',
    config: d?.config ? JSON.stringify(d.config, null, 2) : '',
    notes: d?.notes || ''
  };
}

export function DeviceDrawer({ open, device, onClose }) {
  const isEdit = !!device;
  const create = useCreateDevice();
  const update = useUpdateDevice();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({ defaultValues: defaults(device) });

  useEffect(() => {
    if (open) reset(defaults(device));
  }, [open, device, reset]);

  const submit = handleSubmit(async (values) => {
    let configObj;
    if (values.config?.trim()) {
      try {
        configObj = JSON.parse(values.config);
      } catch {
        // surface a soft inline error via setting a placeholder; toast handled by api errors otherwise
        return;
      }
    }
    const body = {
      name: values.name.trim(),
      deviceId: values.deviceId.trim(),
      serialNumber: values.serialNumber.trim() || undefined,
      type: values.type,
      location: values.location.trim() || undefined,
      ipAddress: values.ipAddress.trim() || undefined,
      config: configObj,
      notes: values.notes.trim() || undefined
    };
    if (isEdit) {
      await update.mutateAsync({ id: device._id || device.id, body }, { onSuccess: () => onClose?.() });
    } else {
      await create.mutateAsync(body, { onSuccess: () => onClose?.() });
    }
  });

  const submitting = create.isPending || update.isPending;

  return (
    <Drawer
      open={open}
      title={isEdit ? 'تعديل جهاز' : 'جهاز جديد'}
      onClose={onClose}
      width={520}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            إلغاء
          </button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={submitting}>
            {submitting ? 'جاري الحفظ...' : isEdit ? 'حفظ' : 'إنشاء'}
          </button>
        </>
      }
    >
      <form onSubmit={submit} noValidate className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الاسم *</label>
            <input className="input" {...register('name', { required: true })} />
            {errors.name ? <div className="text-red text-[11px] mt-1">مطلوب</div> : null}
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">المعرّف *</label>
            <input className="input" {...register('deviceId', { required: true })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الرقم التسلسلي</label>
            <input className="input" {...register('serialNumber')} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">النوع *</label>
            <select className="input" {...register('type')}>
              {TYPES.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الموقع</label>
            <input className="input" placeholder="بوابة الدخول" {...register('location')} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">عنوان IP</label>
            <input className="input" placeholder="192.168.1.x" {...register('ipAddress')} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">
            الإعدادات (JSON اختياري)
          </label>
          <textarea
            className="input font-mono"
            rows={4}
            placeholder='{"port":9090}'
            {...register('config')}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات</label>
          <textarea className="input" rows={2} {...register('notes')} />
        </div>
      </form>
    </Drawer>
  );
}
